import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import type { ReactNode } from "react";

import { AppProviders } from "@/providers/AppProviders";

import appCss from "../styles.css?url";

export const Route = createRootRoute({
    head: () => ({
        meta: [
            {
                charSet: "utf-8",
            },
            {
                name: "viewport",
                content: "width=device-width, initial-scale=1",
            },
            {
                title: "DiningMate",
            },
            { name: "apple-mobile-web-app-capable", content: "yes" },
            { name: "apple-mobile-web-app-status-bar-style", content: "default" },
            { name: "apple-mobile-web-app-title", content: "DiningMate" },
            {
                name: "description",
                content:
                    "Smart restaurant reservations for Australian hospitality. Manage bookings, reduce no-shows, and delight your guests — all in one place.",
            },
            // Open Graph
            { property: "og:type", content: "website" },
            { property: "og:url", content: "https://diningmate.com.au" },
            { property: "og:title", content: "DiningMate — Smart Restaurant Reservations" },
            {
                property: "og:description",
                content:
                    "Smart restaurant reservations for Australian hospitality. Manage bookings, reduce no-shows, and delight your guests — all in one place.",
            },
            {
                property: "og:image",
                content: "https://diningmate.com.au/og-image.png",
            },
            { property: "og:image:width", content: "1200" },
            { property: "og:image:height", content: "630" },
            { property: "og:site_name", content: "DiningMate" },
            { property: "og:locale", content: "en_AU" },
            // Twitter / X
            { name: "twitter:card", content: "summary_large_image" },
            { name: "twitter:title", content: "DiningMate — Smart Restaurant Reservations" },
            {
                name: "twitter:description",
                content:
                    "Smart restaurant reservations for Australian hospitality. Manage bookings, reduce no-shows, and delight your guests — all in one place.",
            },
            { name: "twitter:image", content: "https://diningmate.com.au/og-image.png" },
        ],
        links: [
            {
                rel: "stylesheet",
                href: appCss,
            },
            {
                rel: "icon",
                type: "image/svg+xml",
                href: "/favicon.svg",
            },
            {
                rel: "apple-touch-icon",
                href: "/apple-touch-icon.png",
            },
            {
                rel: "manifest",
                href: "/manifest.json",
            },
            {
                rel: "preconnect",
                href: "https://fonts.googleapis.com",
            },
            {
                rel: "preconnect",
                href: "https://fonts.gstatic.com",
                crossOrigin: "anonymous",
            },
            {
                rel: "stylesheet",
                href: "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap",
            },
        ],
    }),

    notFoundComponent: NotFound,
    shellComponent: RootDocument,
});

function RootDocument({ children }: { children: ReactNode }) {
    const isDev = import.meta.env.DEV;
    const contentSecurityPolicy = [
        "default-src 'self'",
        // posthog-js는 session recording/toolbar를 https://*.i.posthog.com 에서 동적 로드함
        "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://challenges.cloudflare.com https://*.i.posthog.com https://*.posthog.com https://js.stripe.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "font-src 'self' data: https://fonts.gstatic.com",
        `img-src 'self' data: blob: https: ${isDev ? "http://127.0.0.1:54321 http://localhost:54321" : ""}`.trim(),
        "connect-src 'self' http://127.0.0.1:54321 http://localhost:54321 ws://127.0.0.1:54321 ws://localhost:54321 ws://localhost:3000 ws://127.0.0.1:3000 https://challenges.cloudflare.com https://api.iconify.design https://api.simplesvg.com https://api.unisvg.com https://*.supabase.co wss://*.supabase.co https://*.i.posthog.com https://*.posthog.com https://*.ingest.sentry.io https://api.stripe.com",
        "worker-src blob:",
        "frame-src https://challenges.cloudflare.com https://js.stripe.com https://hooks.stripe.com",
        "object-src 'none'",
        "base-uri 'self'",
    ].join("; ");

    return (
        <html lang="en" suppressHydrationWarning>
            <head>
                <HeadContent />
                <meta name="emotion-insertion-point" content="emotion-insertion-point" />
                <meta httpEquiv="Content-Security-Policy" content={contentSecurityPolicy} />
            </head>
            {/* suppressHydrationWarning: 브라우저 확장 프로그램이 <html>/<body>에 속성을
                주입할 때 React hydration 경고/에러를 억제. 자식에는 영향 없음. */}
            <body suppressHydrationWarning>
                <AppProviders>
                    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                        {children}
                    </Sentry.ErrorBoundary>
                </AppProviders>
                {isDev ? (
                    <TanStackDevtools
                        config={{
                            position: "bottom-right",
                        }}
                        plugins={[
                            {
                                name: "Tanstack Router",
                                render: <TanStackRouterDevtoolsPanel />,
                            },
                        ]}
                    />
                ) : null}
                <Scripts />
            </body>
        </html>
    );
}

function ErrorFallback() {
    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                padding: "24px",
                textAlign: "center",
                background: "rgb(251, 250, 248)",
                fontFamily: "'Nunito', system-ui, sans-serif",
            }}
        >
            <div style={{ fontSize: "72px", lineHeight: 1 }}>🍽️</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: "#212B36" }}>
                    Oops! Something went wrong
                </h1>
                <p style={{ margin: 0, fontSize: "16px", color: "#637381", maxWidth: "360px" }}>
                    Our kitchen hit a snag. Please refresh the page and we&apos;ll get things
                    sorted.
                </p>
            </div>
            <button
                onClick={() => window.location.reload()}
                style={{
                    marginTop: "4px",
                    padding: "12px 28px",
                    background: "#e87a30",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "15px",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(232,122,48,0.35)",
                }}
            >
                Refresh page
            </button>
        </div>
    );
}

function NotFound() {
    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                padding: "24px",
                textAlign: "center",
                background: "rgb(251, 250, 248)",
                fontFamily: "'Nunito', system-ui, sans-serif",
            }}
        >
            <div style={{ fontSize: "72px", lineHeight: 1 }}>🔍</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: "#212B36" }}>
                    Page not found
                </h1>
                <p style={{ margin: 0, fontSize: "16px", color: "#637381", maxWidth: "360px" }}>
                    Looks like this table doesn&apos;t exist. Let&apos;s get you back on the menu.
                </p>
            </div>
            <a
                href="/"
                style={{
                    marginTop: "4px",
                    padding: "12px 28px",
                    background: "#e87a30",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: "12px",
                    fontSize: "15px",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    boxShadow: "0 4px 14px rgba(232,122,48,0.35)",
                }}
            >
                Go home
            </a>
        </div>
    );
}
